// guestUtils.ts - Utility functions for managing guest users

/**
 * Generate a unique guest ID with format: guest_UUID
 * The "guest_" prefix allows us to identify guest users
 */
export const generateGuestId = (): string => {
  const uuid = crypto.randomUUID();
  return `guest_${uuid}`;
};

/**
 * Check if a userId is a guest user
 */
export const isGuestUser = (userId: string): boolean => {
  return userId.startsWith('guest_') || userId === 'guest';
};

/**
 * Get or create a guest ID from localStorage
 * This persists the guest ID across page refreshes
 */
export const getOrCreateGuestId = (): string => {
  const STORAGE_KEY = 'boatsconnect_guest_id';
  
  let guestId = localStorage.getItem(STORAGE_KEY);
  
  if (!guestId) {
    guestId = generateGuestId();
    localStorage.setItem(STORAGE_KEY, guestId);
  }
  
  return guestId;
};

/**
 * Get the number of messages sent by the current guest
 */
export const getGuestMessageCount = (guestId: string): number => {
  const STORAGE_KEY = `boatsconnect_guest_messages_${guestId}`;
  const count = localStorage.getItem(STORAGE_KEY);
  return count ? parseInt(count, 10) : 0;
};

/**
 * Increment the guest message count
 */
export const incrementGuestMessageCount = (guestId: string): number => {
  const STORAGE_KEY = `boatsconnect_guest_messages_${guestId}`;
  const currentCount = getGuestMessageCount(guestId);
  const newCount = currentCount + 1;
  localStorage.setItem(STORAGE_KEY, newCount.toString());
  return newCount;
};

/**
 * Reset guest message count (useful for testing)
 */
export const resetGuestMessageCount = (guestId: string): void => {
  const STORAGE_KEY = `boatsconnect_guest_messages_${guestId}`;
  localStorage.removeItem(STORAGE_KEY);
};

/**
 * Check if guest has reached message limit
 */
export const hasReachedGuestLimit = (guestId: string, limit: number = 5): boolean => {
  return getGuestMessageCount(guestId) >= limit;
};
